import { useState } from "react";
import { toast } from "sonner";
import { Volume2, Check, Star, Trophy, Send, Loader2 } from "lucide-react";
import { submitGuideFeedback } from "@/lib/api";

const LANGS = [
  { code: "fr", label: "Français" },
  { code: "wo", label: "Wolof" },
  { code: "di", label: "Dioula" },
  { code: "bm", label: "Bambara" },
];

const T = {
  fr: {
    title: "Activer la biométrie en 4 étapes",
    subtitle: "Suivez les étapes ci-dessous pour sécuriser votre compte.",
    steps: [
      { title: "Étape 1 : Code d'activation", text: "Recevez un code par SMS sur votre numéro de téléphone.", hint: "Exemple : 123456" },
      { title: "Étape 2 : Empreinte digitale", text: "Posez votre doigt sur le capteur biométrique de votre téléphone.", hint: "Maintenez jusqu'à la vibration." },
      { title: "Étape 3 : Photo d'identification", text: "Placez votre visage dans le cercle à l'écran.", hint: "Regardez bien la caméra avant." },
      { title: "Étape 4 : Clé de sécurité", text: "Votre clé de sécurité est générée automatiquement !", hint: "Votre empreinte ne quitte jamais votre téléphone." },
    ],
  },
  wo: {
    title: "Jëkkër gi biométri ci 4 nataal",
    subtitle: "Dëggale yi ci suuf ci 4 ay nataal ci sa liggéey yi.",
    steps: [
      { title: "Nataal 1 : Koodu yi SMS", text: "Jëkk ci SMS ci sa numéro télefon.", hint: "Maa ngi : 123456" },
      { title: "Nataal 2 : Tëgg ci ngëlëm", text: "Tëgg ci ngëlëm ci sa télefon.", hint: "Dëggale ngëlëm sa télefon saama." },
      { title: "Nataal 3 : Pik ci woon", text: "Dëggale woon ci sa télefon.", hint: "Dëggale woon saama ci sa télefon." },
      { title: "Nataal 4 : Koodu sa liggéey", text: "Koodu sa liggéey !", hint: "Sa ngëlëm du génn sa télefon mukk." },
    ],
  },
  di: {
    title: "Tɔ́ɔrɔn yi biométri ci 4 nataal",
    subtitle: "Sɔ́gɔn yi ci suuf ci 4 ay nataal ci sa liggéey yi.",
    steps: [
      { title: "Nataal 1 : Koodu yi sɔ́gɔn", text: "Jɛ́kk ci SMS ci sa numéro télefon.", hint: "A yɔrɔ : 123456" },
      { title: "Nataal 2 : Tɛ́ɛn ci ngɛlɛm", text: "Tɛ́ɛn ci ngɛlɛm ci sa télefon.", hint: "Dɔ́ɔn ci ngɛlɛm sa télefon saama." },
      { title: "Nataal 3 : Dɔ́ɔn ci woon", text: "Dɔ́ɔn ci woon ci sa télefon.", hint: "Dɔ́ɔn ci woon saama ci sa télefon." },
      { title: "Nataal 4 : Koodu sa liggéey", text: "Koodu sa liggéey !", hint: "Sa ngɛlɛm tɛ bɔ sa télefon fewu." },
    ],
  },
  bm: {
    title: "Kɔnɔ yi biométri ci 4 nataal",
    subtitle: "Bɔ kɔnɔ yi ci suuf ci 4 ay nataal ci sa liggéey yi.",
    steps: [
      { title: "Nataal 1 : Koodu yi bɔ", text: "Bɔ koodu ci SMS ci sa numéro télefon.", hint: "A yɔrɔ : 123456" },
      { title: "Nataal 2 : Bɔ ngɛlɛm", text: "Bɔ ngɛlɛm ci sa télefon.", hint: "Bɔ ngɛlɛm sa télefon saama." },
      { title: "Nataal 3 : Bɔ woon", text: "Bɔ woon ci sa télefon.", hint: "Bɔ woon saama ci sa télefon." },
      { title: "Nataal 4 : Koodu sa bɔ", text: "Koodu sa bɔ !", hint: "I ngɛlɛm tɛ bɔ i télefon la abada." },
    ],
  },
};

const StepSvg = ({ index }) => {
  const svgs = [
    <svg key="s1" viewBox="0 0 200 120" className="w-full h-auto" role="img" aria-label="Code SMS">
      <rect width="200" height="120" fill="#f0f8ff" rx="10" />
      <rect x="25" y="18" width="150" height="28" fill="#2E7D32" rx="6" />
      <text x="100" y="37" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">SMS : 123456</text>
      <rect x="25" y="58" width="150" height="44" fill="white" stroke="#ddd" rx="6" />
      <rect x="45" y="72" width="110" height="16" fill="#f0f0f0" rx="4" />
      <circle cx="100" cy="80" r="5" fill="#2196F3" />
    </svg>,
    <svg key="s2" viewBox="0 0 200 120" className="w-full h-auto" role="img" aria-label="Empreinte digitale">
      <rect width="200" height="120" fill="#fff9f5" rx="10" />
      <circle cx="100" cy="60" r="42" fill="#f0f0f0" stroke="#ddd" strokeWidth="2" />
      <path d="M100 30 a30 30 0 0 1 30 30 a30 30 0 0 1 -30 30" fill="none" stroke="#2E7D32" strokeWidth="4" strokeLinecap="round" />
      <path d="M100 40 a20 20 0 0 1 20 20 a20 20 0 0 1 -20 20" fill="none" stroke="#4CAF50" strokeWidth="4" strokeLinecap="round" />
      <path d="M100 50 a10 10 0 0 1 10 10 a10 10 0 0 1 -10 10" fill="none" stroke="#81C784" strokeWidth="4" strokeLinecap="round" />
    </svg>,
    <svg key="s3" viewBox="0 0 200 120" className="w-full h-auto" role="img" aria-label="Photo du visage">
      <rect width="200" height="120" fill="#f0fff4" rx="10" />
      <circle cx="100" cy="58" r="36" fill="none" stroke="#2196F3" strokeWidth="3" strokeDasharray="6 4" />
      <circle cx="100" cy="50" r="13" fill="#90CAF9" />
      <path d="M78 84 a22 14 0 0 1 44 0" fill="#90CAF9" />
    </svg>,
    <svg key="s4" viewBox="0 0 200 120" className="w-full h-auto" role="img" aria-label="Clé de sécurité">
      <rect width="200" height="120" fill="#f5f3ff" rx="10" />
      <rect x="35" y="30" width="130" height="34" fill="#2196F3" rx="6" />
      <text x="100" y="52" textAnchor="middle" fill="white" fontSize="13" fontWeight="bold">A1B2-C3D4</text>
      <circle cx="70" cy="90" r="12" fill="none" stroke="#2E7D32" strokeWidth="4" />
      <rect x="80" y="87" width="45" height="6" fill="#2E7D32" rx="2" />
      <rect x="118" y="87" width="6" height="14" fill="#2E7D32" rx="2" />
      <rect x="108" y="87" width="6" height="11" fill="#2E7D32" rx="2" />
    </svg>,
  ];
  return svgs[index];
};

const STEP_COLORS = ["from-sky-500 to-blue-400", "from-emerald-500 to-teal-400", "from-orange-500 to-amber-400", "from-violet-500 to-purple-400"];
const STEP_POINTS = [10, 20, 30, 40];
const COMPLETION_BONUS = 100;
const PROGRESS_KEY = "bio_guide_progress";

export const BiometricGuide = () => {
  const [lang, setLang] = useState(() => localStorage.getItem("bio_guide_lang") || "fr");
  const [done, setDone] = useState(() => {
    try { return JSON.parse(localStorage.getItem(PROGRESS_KEY)) || []; } catch { return []; }
  });
  const t = T[lang];
  const points = done.reduce((s, i) => s + STEP_POINTS[i], 0) + (done.length === 4 ? COMPLETION_BONUS : 0);
  const pct = (done.length / 4) * 100;

  const pick = (code) => {
    setLang(code);
    localStorage.setItem("bio_guide_lang", code);
  };

  const markDone = (i) => {
    if (done.includes(i)) return;
    const next = [...done, i];
    setDone(next);
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(next));
    if (next.length === 4) {
      toast.success(`🎉 Guide terminé ! Badge final débloqué (+${COMPLETION_BONUS} pts). Activez maintenant votre biométrie.`);
    } else {
      toast.success(`🏅 Badge débloqué : étape ${i + 1} (+${STEP_POINTS[i]} pts)`);
    }
  };

  const playAudio = (i) => {
    const audio = new Audio(`/audio/guide/step${i + 1}_${lang}.mp3`);
    audio.onerror = () => toast.info("Audio bientôt disponible dans cette langue.");
    audio.play().catch(() => toast.info("Audio bientôt disponible dans cette langue."));
  };

  return (
    <div data-testid="biometric-guide">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {LANGS.map((l) => (
          <button
            key={l.code}
            type="button"
            onClick={() => pick(l.code)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              lang === l.code ? "bg-emerald-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            data-testid={`guide-lang-${l.code}`}
          >
            {l.label}
          </button>
        ))}
      </div>

      <div className="font-bold text-sm mb-1" data-testid="guide-title">{t.title}</div>
      <p className="text-xs text-[var(--ink-soft)] mb-3">{t.subtitle}</p>

      <div className="flex items-center gap-3 mb-4" data-testid="guide-progress">
        <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-600 shrink-0" data-testid="guide-points">
          <Trophy size={13} /> {points} pts
        </span>
      </div>

      {done.length === 4 && (
        <div className="mb-4 rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm font-semibold text-emerald-700" data-testid="guide-completed-banner">
          🎉 Guide terminé — tous les badges débloqués ! Appuyez sur « Activer l&apos;empreinte digitale » ci-dessous.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {t.steps.map((s, i) => {
          const unlocked = done.includes(i);
          return (
            <div key={i} className={`rounded-2xl border p-3 transition-colors ${unlocked ? "border-emerald-300 bg-emerald-50/50" : "border-[var(--line)] bg-white/70"}`} data-testid={`guide-step-${i + 1}`}>
              <div className="flex items-center justify-between mb-2">
                <div className={`inline-block text-[10px] font-bold text-white px-2 py-0.5 rounded-full bg-gradient-to-r ${STEP_COLORS[i]}`}>
                  {i + 1}/4
                </div>
                {unlocked && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700" data-testid={`guide-badge-${i + 1}`}>
                    🏅 +{STEP_POINTS[i]} pts
                  </span>
                )}
              </div>
              <StepSvg index={i} />
              <div className="font-bold text-sm mt-2">{s.title}</div>
              <p className="text-xs text-[var(--ink-soft)] mt-1 leading-relaxed">{s.text}</p>
              <p className="text-[11px] text-gray-400 mt-1 italic">{s.hint}</p>
              <div className="flex items-center gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => playAudio(i)}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-semibold bg-sky-50 text-sky-700 hover:bg-sky-100 transition-colors"
                  data-testid={`guide-audio-${i + 1}`}
                >
                  <Volume2 size={12} /> Écouter
                </button>
                <button
                  type="button"
                  onClick={() => markDone(i)}
                  disabled={unlocked}
                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-semibold transition-colors ${
                    unlocked ? "bg-emerald-100 text-emerald-700 cursor-default" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                  data-testid={`guide-done-${i + 1}`}
                >
                  <Check size={12} /> {unlocked ? "Fait" : "J'ai compris"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <FeedbackWidget />
    </div>
  );
};

function FeedbackWidget() {
  const [sent, setSent] = useState(() => localStorage.getItem("bio_guide_feedback_done") === "1");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  if (sent) {
    return (
      <p className="text-xs text-emerald-700 font-semibold mt-4" data-testid="guide-feedback-thanks">
        ✅ Merci pour votre avis sur ce guide !
      </p>
    );
  }

  const send = async () => {
    if (!rating) {
      toast.error("Choisissez une note de 1 à 5 étoiles");
      return;
    }
    setBusy(true);
    try {
      await submitGuideFeedback({ rating, comment, language: localStorage.getItem("bio_guide_lang") || "fr" });
      localStorage.setItem("bio_guide_feedback_done", "1");
      setSent(true);
      toast.success("Merci ! Votre avis nous aide à améliorer le guide.");
    } catch {
      toast.error("Erreur d'envoi, réessayez.");
    }
    setBusy(false);
  };

  return (
    <div className="mt-4 rounded-2xl border border-[var(--line)] bg-white/70 p-4" data-testid="guide-feedback">
      <div className="font-bold text-sm mb-2">Ce guide vous a-t-il aidé ?</div>
      <div className="flex items-center gap-1 mb-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => setRating(n)} data-testid={`guide-star-${n}`} aria-label={`${n} étoiles`}>
            <Star size={22} className={n <= rating ? "fill-amber-400 text-amber-400" : "text-gray-300"} />
          </button>
        ))}
      </div>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value.slice(0, 500))}
        placeholder="Un commentaire ? (optionnel)"
        rows={2}
        className="w-full text-xs rounded-xl border border-[var(--line)] p-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-300"
        data-testid="guide-feedback-comment"
      />
      <button
        type="button"
        onClick={send}
        disabled={busy}
        className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50"
        data-testid="guide-feedback-submit"
      >
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Envoyer mon avis
      </button>
    </div>
  );
}
